import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const GRID = 48;
const BG  = '#0C0F1A';

interface Pulse {
  axis:    'h' | 'v';
  linePos: number;      // y for horizontal, x for vertical
  head:    number;      // current leading edge position
  dir:     1 | -1;
  speed:   number;
  alpha:   number;
  r: number; g: number; b: number;
  tail:    number;      // trail length in px
}

export default function LoginView() {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const unlockRef  = useRef(false);
  const [value,  setValue]  = useState('');
  const [phase,  setPhase]  = useState<'enter' | 'idle' | 'unlock'>('enter');
  const navigate = useNavigate();

  // If already authed, skip straight through
  useEffect(() => {
    if (localStorage.getItem('lunar-grid-authenticated')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Trigger entry animation on mount
  useEffect(() => {
    const t = setTimeout(() => setPhase('idle'), 60);
    return () => clearTimeout(t);
  }, []);

  // Canvas — grid + photon pulses
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    let raf: number;
    let pulses: Pulse[] = [];
    let frame = 0;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const spawn = () => {
      const W = canvas.width, H = canvas.height;
      const cx = W / 2, cy = H / 2;
      const radius = Math.min(W, H) * 0.42;

      const isH = Math.random() > 0.5;
      const maxN = Math.floor(radius / GRID);
      const n    = Math.floor(Math.random() * (maxN * 2 + 1)) - maxN;
      const linePos = isH ? cy + n * GRID : cx + n * GRID;

      // Only spawn lines inside the visible radius
      const perpDist = isH ? Math.abs(linePos - cy) : Math.abs(linePos - cx);
      if (perpDist > radius * 0.9) return;

      const dir: 1 | -1 = Math.random() > 0.5 ? 1 : -1;
      const isBlue       = Math.random() > 0.28;

      pulses.push({
        axis:    isH ? 'h' : 'v',
        linePos,
        head:    dir === 1 ? -90 : (isH ? W + 90 : H + 90),
        dir,
        speed:   2.8 + Math.random() * 3.2,
        alpha:   0.42 + Math.random() * 0.52,
        r:       isBlue ?  59 : 110,
        g:       isBlue ? 110 : 195,
        b:       isBlue ? 245 : 255,
        tail:    65 + Math.random() * 95,
      });
    };

    const draw = () => {
      raf = requestAnimationFrame(draw);
      frame++;

      const W = canvas.width, H = canvas.height;
      const cx = W / 2, cy = H / 2;
      const radius = Math.min(W, H) * 0.43;

      ctx.clearRect(0, 0, W, H);

      // ── Grid lines (centered on canvas center, radial fade) ──
      const gridAlphaBase = unlockRef.current ? 0.18 : 0.10;

      for (let n = 0; ; n++) {
        const xs = n === 0 ? [cx] : [cx + n * GRID, cx - n * GRID];
        if (n * GRID > W / 2 + GRID) break;
        for (const x of xs) {
          const d = Math.abs(x - cx);
          if (d > radius) continue;
          const fade = Math.pow(1 - d / radius, 2);
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, H);
          ctx.strokeStyle = `rgba(59,110,245,${gridAlphaBase * fade})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      for (let n = 0; ; n++) {
        const ys = n === 0 ? [cy] : [cy + n * GRID, cy - n * GRID];
        if (n * GRID > H / 2 + GRID) break;
        for (const y of ys) {
          const d = Math.abs(y - cy);
          if (d > radius) continue;
          const fade = Math.pow(1 - d / radius, 2);
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(W, y);
          ctx.strokeStyle = `rgba(59,110,245,${gridAlphaBase * fade})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      // ── Spawn ──
      const spawnRate = unlockRef.current ? 4 : 26;
      if (frame % spawnRate === 0) {
        spawn();
        if (unlockRef.current || Math.random() > 0.55) spawn();
      }

      // ── Pulses ──
      pulses = pulses.filter(p => {
        p.head += p.speed * p.dir;

        // Cull when fully off-canvas
        if (p.dir === 1 && p.head > (p.axis === 'h' ? W : H) + p.tail + 60) return false;
        if (p.dir === -1 && p.head < -p.tail - 60) return false;

        const perpDist = p.axis === 'h'
          ? Math.abs(p.linePos - cy)
          : Math.abs(p.linePos - cx);
        if (perpDist > radius) return false;

        const lineFade  = Math.pow(Math.max(0, 1 - perpDist / radius), 1.6);
        const glowBoost = unlockRef.current ? 1.6 : 1;
        const { r, g, b, alpha, tail, head, dir, axis, linePos } = p;
        const tailEnd = head - tail * dir;

        // Gradient direction: always fades from 0 (tail) → alpha (head)
        let gx1: number, gy1: number, gx2: number, gy2: number;
        if (axis === 'h') {
          gx1 = dir === 1 ? tailEnd : head;   gy1 = linePos;
          gx2 = dir === 1 ? head    : tailEnd; gy2 = linePos;
        } else {
          gx1 = linePos; gy1 = dir === 1 ? tailEnd : head;
          gx2 = linePos; gy2 = dir === 1 ? head    : tailEnd;
        }

        const grad = ctx.createLinearGradient(gx1, gy1, gx2, gy2);
        grad.addColorStop(0,   `rgba(${r},${g},${b},0)`);
        grad.addColorStop(0.55, `rgba(${r},${g},${b},${alpha * lineFade * 0.45 * glowBoost})`);
        grad.addColorStop(1,   `rgba(${r},${g},${b},${alpha * lineFade * glowBoost})`);

        ctx.beginPath();
        if (axis === 'h') {
          ctx.moveTo(gx1, linePos);
          ctx.lineTo(gx2, linePos);
        } else {
          ctx.moveTo(linePos, gy1);
          ctx.lineTo(linePos, gy2);
        }
        ctx.strokeStyle = grad;
        ctx.lineWidth = unlockRef.current ? 2.5 : 1.8;
        ctx.stroke();

        // Head radial glow
        const hx   = axis === 'h' ? head    : linePos;
        const hy   = axis === 'h' ? linePos : head;
        const glowR = unlockRef.current ? 12 : 6;
        const gGrad = ctx.createRadialGradient(hx, hy, 0, hx, hy, glowR);
        gGrad.addColorStop(0, `rgba(${r},${g},${b},${alpha * lineFade * glowBoost})`);
        gGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath();
        ctx.arc(hx, hy, glowR, 0, Math.PI * 2);
        ctx.fillStyle = gGrad;
        ctx.fill();

        return true;
      });
    };

    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const unlock = () => {
    if (!value.trim()) return;
    unlockRef.current = true;
    setPhase('unlock');
    localStorage.setItem('lunar-grid-authenticated', '1');
    setTimeout(() => navigate('/', { replace: true }), 920);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    unlock();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: BG, zIndex: 100 }}>
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />

      <div className={`login-stage login-stage--${phase}`}>

        {/* Logo */}
        <div className="login-logo">
          <span className="login-lunar">LUNAR</span>
          <span className="login-logo-break" />
          <span className="login-grid-word">GRID</span>
        </div>

        {/* Separator */}
        <div className="login-sep" />

        {/* Form */}
        <form onSubmit={handleSubmit} className="login-form">
          <span className="login-label">ACCESS KEY</span>
          <div className="login-field">
            <span className="login-caret">›</span>
            <input
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="enter passkey"
              className="login-input"
              autoFocus
              autoComplete="off"
            />
          </div>
        </form>

        <button type="button" onClick={unlock} className="login-hint login-hint--btn">
          ↵ &nbsp; authenticate
        </button>
      </div>
    </div>
  );
}
